/**
 * Core data contracts for First Weeks.
 *
 * Everything the app records is an append-only, last-writer-wins event so
 * two paired devices can merge without conflicts. Events sync as encrypted
 * blobs; the server never sees these shapes.
 */

export type Role = 'mom' | 'partner';
export type Locale = 'en' | 'es';
export type VolumeUnit = 'oz' | 'ml';
export type WeightUnit = 'lboz' | 'g';
export type ThemePref = 'auto' | 'light' | 'dark';

/** Primary color choice. Must match the [data-accent] blocks in global.css. */
export const ACCENTS = ['purple', 'blue', 'teal', 'rose', 'clay'] as const;
export type AccentPref = (typeof ACCENTS)[number];

/** Which phase the family is in. Prenatal until "Baby is here!" is tapped. */
export type Phase = 'prenatal' | 'baby';

export interface Settings {
  locale: Locale;
  volumeUnit: VolumeUnit;
  weightUnit: WeightUnit;
  theme: ThemePref;
  accent: AccentPref;
}

export const DEFAULT_SETTINGS: Settings = {
  locale: 'en',
  volumeUnit: 'oz',
  weightUnit: 'lboz',
  theme: 'auto',
  accent: 'purple',
};

/**
 * Shared family profile. Synced between paired devices (last write wins).
 * Baby name is optional by design; a nickname is encouraged over a legal
 * name so nothing identifying needs to exist even in encrypted form.
 */
export interface Profile {
  phase: Phase;
  /** ISO date (yyyy-mm-dd) */
  dueDate?: string;
  /** ISO datetime with offset; set when baby arrives */
  birthAt?: string;
  babyNickname?: string;
  /** grams */
  birthWeightG?: number;
  feedingGoals?: string;
  /** ISO date the newborn (3-5 day) checkup was booked for, if scheduled */
  newbornCheckupAt?: string;
  /** ISO date of booked lactation follow-up, if scheduled */
  lactationFollowupAt?: string;
  updatedAt: string;
}

/** Local, never-synced device identity. */
export interface DeviceInfo {
  deviceId: string;
  role: Role;
  onboarded: boolean;
}

// ── Events ────────────────────────────────────────────────────────────────

export type EventType = 'feed' | 'diaper' | 'weight' | 'pump' | 'sleep';

interface EventBase {
  /** uuid v4 */
  id: string;
  type: EventType;
  /** ISO datetime with offset — when the thing happened (start time) */
  at: string;
  /** which device recorded it */
  deviceId: string;
  /** who recorded it, for "logged by partner" labels */
  role: Role;
  /** LWW clock — ISO datetime */
  updatedAt: string;
  /** tombstone; deleted events still sync so both devices converge */
  deleted?: boolean;
  note?: string;
}

export type FeedMethod = 'breast' | 'bottle';
export type BreastSide = 'left' | 'right' | 'both';
export type BottleContents = 'breastmilk' | 'formula' | 'mixed';

export interface FeedEvent extends EventBase {
  type: 'feed';
  method: FeedMethod;
  /** breast feeds */
  side?: BreastSide;
  leftSeconds?: number;
  rightSeconds?: number;
  /** which side the feed started on, to suggest the next side */
  startedSide?: 'left' | 'right';
  /** bottle feeds — always stored in ml */
  amountMl?: number;
  contents?: BottleContents;
  /** ISO datetime; present when the feed has ended */
  endAt?: string;
}

export interface DiaperEvent extends EventBase {
  type: 'diaper';
  wet: boolean;
  dirty: boolean;
}

export type WeightContext = 'birth' | 'hospital' | 'office' | 'home';

export interface WeightEvent extends EventBase {
  type: 'weight';
  grams: number;
  context?: WeightContext;
}

export interface PumpEvent extends EventBase {
  type: 'pump';
  /** ml */
  leftMl?: number;
  rightMl?: number;
  durationSeconds?: number;
}

export interface SleepEvent extends EventBase {
  type: 'sleep';
  /** ISO datetime; absent while the sleep is ongoing */
  endAt?: string;
}

export type TrackedEvent =
  | FeedEvent
  | DiaperEvent
  | WeightEvent
  | PumpEvent
  | SleepEvent;

// ── Sync ──────────────────────────────────────────────────────────────────

/**
 * What travels between paired devices, AES-GCM encrypted, via the relay.
 * Profile changes ride along with tracked events so both devices agree on
 * birth date, checkup bookings, etc.
 */
export type SyncPayload =
  | { kind: 'event'; event: TrackedEvent }
  | { kind: 'profile'; profile: Profile };

export interface PairingState {
  /** the human-enterable code; kept only on-device */
  code: string;
  /** hex SHA-256 derived from the code — the server-side identifier */
  pairId: string;
  /** highest relay sequence number we have already applied */
  cursor: number;
  /** relay base URL */
  serverUrl: string;
  connectedAt: string;
}

// ── Wire format (what the relay stores; blobs are opaque to it) ──────────

export interface WireEvent {
  /** client id used for idempotent appends (event id or profile singleton id) */
  id: string;
  /** base64 iv + ciphertext of a SyncPayload */
  blob: string;
}

export interface WireEventOut extends WireEvent {
  seq: number;
}
